import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import { withPositionsAbsoluteSort } from '@src/utils/columns/absoluteSortEligibility';
import {
  getAgGridColId,
  isTalosUser,
  useDefaultColumns,
  type Column,
  type ColumnDef,
  type Position,
  type PredictionMarketColumnParams,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag, useUser } from '../../../../hooks';
import { positionAmountColumnParams, predictionPositionAssetColumnParams } from '../positionColumnRendering';
import type { PositionColumn } from '../types';

const PREDICTION_GROUPING_COLUMN_PARAMS: PredictionMarketColumnParams = {
  symbolField: 'Asset',
};

export const usePositionsPredictionsColumns = (defaultColumns: PositionColumn[]): Column[] => {
  const user = useUser();
  const { homeCurrency } = useDisplaySettings();
  const { showPositionsBlotterWarningColumn } = useFeatureFlag();

  const columnDefinitions = useMemo(() => {
    return new Map<string, Column>(
      (
        compact([
          showPositionsBlotterWarningColumn && {
            id: 'reconWarning',
            type: 'reconWarning',
            field: 'reconWarning',
            aggregate: true,
            sortable: true,
          },
          {
            id: 'predictionCategory',
            type: 'predictionCategory',
            title: 'Category',
            initialHide: true,
            params: PREDICTION_GROUPING_COLUMN_PARAMS,
          },
          {
            id: 'predictionSeries',
            type: 'predictionSeries',
            title: 'Series',
            initialHide: true,
            params: PREDICTION_GROUPING_COLUMN_PARAMS,
          },
          {
            id: 'predictionEvent',
            type: 'predictionEvent',
            title: 'Event',
            initialHide: true,
            params: PREDICTION_GROUPING_COLUMN_PARAMS,
          },
          {
            id: 'instrument',
            field: 'Asset',
            type: 'asset',
            sortable: true,
            title: 'Instrument',
            initialWidth: 200,
            params: predictionPositionAssetColumnParams,
          },
          {
            id: 'MarketAccount',
            field: 'MarketAccount',
            type: 'marketAccount',
            sortable: true,
            initialSort: '-',
          },
          {
            id: 'Amount',
            field: 'NotionalAmount',
            type: 'size',
            title: 'Position',
            sortable: true,
            aggregate: true,
            initialWidth: 150,
            params: positionAmountColumnParams,
            description: 'Size of the position in currency terms.',
          },
          {
            field: 'AvgCost',
            type: 'price',
            initialWidth: 150,
            params: {
              quoteCurrencyField: 'AvgCostCurrency',
            },
            description: 'Average cost for the instrument provided by the venue.',
          },
          {
            field: 'MarkPrice',
            type: 'price',
            initialWidth: 110,
            params: {
              quoteCurrencyField: 'MarkPriceCurrency',
            },
            description: 'Most recent mark price for the instrument provided by the venue.',
          },
          {
            field: 'UnrealizedPnL',
            type: 'size',
            title: 'Unrealized PnL',
            params: {
              currencyField: 'PnLCurrency',
              highlightNegative: true,
            },
            description:
              'Unrealized P&L provided by the venue for the current position. The type of P&L (Life-To-Date or Session) is determined by the venue.',
          },
          {
            field: 'Equivalent.UnrealizedPnL',
            type: 'size',
            title: `Unrealized PnL (${homeCurrency})`,
            aggregate: true,
            params: {
              currencyField: 'Equivalent.Currency',
              highlightNegative: true,
            },
            initialHide: true,
            description: 'Unrealized P&L for current position converted to home currency.',
          },
          {
            field: 'LastUpdateTime',
            type: 'date',
            initialHide: true,
          },
          isTalosUser(user) && {
            field: 'Status',
            type: 'text',
            initialHide: true,
          },
          isTalosUser(user) && {
            field: 'PositionSource',
            type: 'text',
            initialHide: true,
          },
        ] satisfies (ColumnDef<Position> | false)[]) satisfies ColumnDef<Position>[]
      ).map(c => [getAgGridColId(c), withPositionsAbsoluteSort(c)])
    );
  }, [showPositionsBlotterWarningColumn, homeCurrency, user]);

  return useDefaultColumns(defaultColumns, columnDefinitions);
};
